import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@eventbrite/eds-modal';
import './page.scss';
import { Structure } from '@eventbrite/eds-structure';
import { translationPropType } from '@eventbrite/i18n';
import { THEME_SETTINGS_PROP } from '../constants';
import { LeaveCheckoutWarning } from '../components/leaveCheckoutWarning';
import { StatsigStableIDVisualizerTool } from '@eventbrite/statsig-stable-id-visualizer-tool';
import RebrandingCloseIcon from '../resources/rebranding-close-icon';
import ReCaptchaV3 from '../components/ReCaptcha';
import { getWindowObject } from '@eventbrite/feature-detection';
import { getShouldShowPrivacyBanner } from '../containers/series/utils/getShouldShowPrivacyBanner';

const POLL_THRESHOLD = 10000;

const ThemeWrapper = ({ themeSettings, children }) => {
    if (themeSettings) {
        return (
            <Structure rootTheme={themeSettings} fullScreenMain={true}>
                {children}
            </Structure>
        );
    }
    return children;
};

const CheckoutContainer = ({
    children,
    isAppRebrandActive,
    isModal,
    isNativeApp,
    isOnlyCheckoutPage,
    isOrderSuccessAnimationPage,
    isOrganizerQuestionsPage,
    isTicketsCompactPage,
    isOnlyCheckoutFlow,
    isTicketSelectionCompactSizeFlow,
    modalAriaLabel,
    onClose,
    privacyBannerDisplayed,
    themeSettings,
}) => {
    if (isTicketsCompactPage) {
        return (
            <div
                data-reactroot
                className="tickets-compact-page eds-bg-color--white"
                data-automation="tickets-compact-page"
                data-testid="tickets-compact-page"
            >
                {children}
            </div>
        );
    }
    if (isOnlyCheckoutPage) {
        return (
            <div data-reactroot data-testid="only-checkout-page">
                {children}
            </div>
        );
    }

    const isClosable = !(
        isTicketsCompactPage ||
        isOrganizerQuestionsPage ||
        isOnlyCheckoutPage ||
        isOrderSuccessAnimationPage
    );

    const focusTrapOptions =
        (isModal || isNativeApp) && isClosable
            ? {
                  // focus-trap **forces** us to define an initial focus node.
                  // It allows customizing the node through the `initialFocus`
                  // property. However, it we pass anything `falsy`,
                  // `focus-trap` will fall back to picking the first element
                  // reachable by tabbing in the page. If we pass a string value
                  // it will use the string as selector and try to find the
                  // element. If we pass a function, focus-trap will execute it
                  // and its return value will be used as `node`. If either the
                  // selector or the function lead to a `falsy` node (aka, to a
                  // non-existent node) then focus-trap throws an exception. If
                  // we pass a valid selector then the Jest functional tests
                  // break, while we change the initial focus to the specified
                  // selector which we might not want. The solution here is
                  // passing a truthy value that is neither a string nor a
                  // function. If the truthy value turns out to not having a
                  // `focus` function, then focus-trap silently skips setting
                  // the initial focus and throws no error nor complain. Also
                  // the Jest functional tests pass.
                  initialFocus: 1,
              }
            : {};

    if (
        isModal ||
        isNativeApp ||
        /*
            This is added to be compatible to how Listings (web) embeds Checkout
            in compact and onlyCheckout size: Listings is not passing the `modal=1` queryString
            parameter for historical reasons. However, when Checkout in compact size
            displays any page but the TicketSelectionCompactSizePage and the OnlyCheckoutPage, we want to
            render the page in a modal, since Listings will move Checkout from a
            smaller container to a modal container. So here we check if we are
            rendering any but the TicketSelectionCompactSizePage in compact size,
            and the OnlyCheckoutPage in onlyCheckout size,
            that is, if we are in a flow that started in the TicketSelectionCompactSizePage
            but we are no longer in the TicketSelectionCompactSizePage or the OnlyCheckoutPage itself
        */
        isTicketSelectionCompactSizeFlow ||
        isOnlyCheckoutFlow
    ) {
        const closeButtonClassName = isAppRebrandActive
            ? 'checkout-rebranding-close-button'
            : null;

        const closeButtonCustomIcon = isAppRebrandActive
            ? RebrandingCloseIcon
            : null;

        return (
            <ThemeWrapper themeSettings={themeSettings}>
                <Modal
                    type="aperture"
                    ariaLabel={modalAriaLabel}
                    ariaModal={true}
                    disableCloseOnClickOutside={true}
                    disableFocusTrap={privacyBannerDisplayed}
                    focusTrapOptions={focusTrapOptions}
                    isShown={true}
                    isClosable={isClosable}
                    modalBodyIsEmpty={true}
                    noPadding={true}
                    onClose={onClose}
                    roleIsDialog={true}
                    closeButtonClassName={closeButtonClassName}
                    closeButtonCustomIcon={closeButtonCustomIcon}
                >
                    {children}
                </Modal>
            </ThemeWrapper>
        );
    }

    return (
        // XXX: The .eds-modal class provides necessary styling to make the fixed header/footer work.
        // That CSS should be factored out into a separate class so we can use that class instead of the
        // modal class. Ticket here: https://jira.evbhome.com/browse/EB-60185
        // Also we need to overwrite fixed position, applied by eds-modal class, because after zooming-in the
        // checkout is not displayed correctly on iOs Version 12.0.
        <ThemeWrapper themeSettings={themeSettings}>
            <div
                data-reactroot
                className="checkout-widget-container eds-modal eds-modal--active eds-bg-color--white u-position-absolute"
                data-automation="checkout-widget-container"
                data-testid="checkout-widget-container"
            >
                {children}
            </div>
        </ThemeWrapper>
    );
};

export class Page extends React.Component {
    static propTypes = {
        /**
         * action to abandon order when closing window
         */
        abandonOrderOnClose: PropTypes.func,
        /**
         * The component to be rendered inside the Page.
         */
        children: PropTypes.element,
        isAddAttendeesOrder: PropTypes.bool.isRequired,
        isAppRebrandActive: PropTypes.bool,
        isModal: PropTypes.bool,
        /**
         * Always show modal view when in native app
         */
        isNativeApp: PropTypes.bool,
        isOrganizerQuestionsPage: PropTypes.bool,
        isOrderSuccessAnimationPage: PropTypes.bool,
        isProductionEnv: PropTypes.bool,
        /**
         * Checkout has started on the ticket selection compact size page
         */
        isTicketSelectionCompactSizeFlow: PropTypes.bool,
        /**
         * The ticket selection compact size page is rendered
         */
        isTicketsCompactPage: PropTypes.bool,
        isOnlyCheckoutPage: PropTypes.bool,
        isTransferredOrder: PropTypes.bool.isRequired,
        /**
         * Denotes that this app is being rendered inside an iframe, giving the url of its parent page
         */
        /**
         * Accessible title for the Modal component
         */
        modalAriaLabel: translationPropType,
        performTabless: PropTypes.bool,
        /**
         * Theme override data for customization
         */
        onClose: PropTypes.func,
        /**
         * Function to be called periodically to let the waiting room know we are alive
         */
        onQueueRenew: PropTypes.func.isRequired,
        routePathname: PropTypes.string,
        themeSettings: THEME_SETTINGS_PROP,
    };

    constructor(props) {
        super(props);

        this.state = {
            privacyBannerDisplayed: false,
        };
    }

    componentDidMount() {
        this._startPoll();

        if (getWindowObject('transcend')) {
            window.transcend.ready((transcend) => {
                this.setState({
                    privacyBannerDisplayed: getShouldShowPrivacyBanner(),
                });

                transcend.addEventListener(
                    'view-state-change',
                    ({ detail: { viewState } }) => {
                        if (viewState === 'Hidden') {
                            this.setState({ privacyBannerDisplayed: false });
                        }
                    },
                );
            });
        }
    }

    componentWillUnmount() {
        clearInterval(this.pollFunction);
    }

    _startPoll() {
        this.props.onQueueRenew();
        this.pollFunction = setInterval(
            () => this.props.onQueueRenew(),
            POLL_THRESHOLD,
        );
    }

    render() {
        const { privacyBannerDisplayed } = this.state;

        const {
            abandonOrderOnClose,
            children,
            isAddAttendeesOrder,
            isModal,
            isNativeApp,
            isOrganizerQuestionsPage,
            isOrderSuccessAnimationPage,
            isProductionEnv,
            isTicketsCompactPage,
            isTicketSelectionCompactSizeFlow,
            isOnlyCheckoutPage,
            isOnlyCheckoutFlow,
            isTransferredOrder,
            modalAriaLabel,
            onClose,
            performTabless,
            routePathname,
            isAppRebrandActive,
        } = this.props;

        const stableIdVisualizerTool = !isProductionEnv ? (
            <StatsigStableIDVisualizerTool isProductionEnv={isProductionEnv} />
        ) : null;

        return (
            <>
                <CheckoutContainer
                    privacyBannerDisplayed={privacyBannerDisplayed}
                    isModal={isModal}
                    isNativeApp={isNativeApp}
                    isTicketSelectionCompactSizeFlow={
                        isTicketSelectionCompactSizeFlow
                    }
                    isTicketsCompactPage={isTicketsCompactPage}
                    isOnlyCheckoutFlow={isOnlyCheckoutFlow}
                    isOnlyCheckoutPage={isOnlyCheckoutPage}
                    isOrderSuccessAnimationPage={isOrderSuccessAnimationPage}
                    modalAriaLabel={modalAriaLabel}
                    onClose={onClose}
                    isOrganizerQuestionsPage={isOrganizerQuestionsPage}
                    isAppRebrandActive={isAppRebrandActive}
                >
                    {children}
                </CheckoutContainer>
                <LeaveCheckoutWarning
                    abandonOrderOnClose={abandonOrderOnClose}
                    isAddAttendeesOrder={isAddAttendeesOrder}
                    isTransferredOrder={isTransferredOrder}
                    performTabless={performTabless}
                    routePathname={routePathname}
                />
                {stableIdVisualizerTool}
                <ReCaptchaV3 />
            </>
        );
    }
}
